import { base44 } from '@/api/base44Client';

// Shared plumbing for canned responses. Every admin reply box loads the same
// list under this key, so the library stays in step across the case workspace,
// the customer profile composer and the inboxes.
export const MACRO_QUERY_KEY = ['crm-macros'];

export const MACRO_CATEGORIES = [
  'Directions',
  'Rescheduling',
  'Hardware Apology',
  'Vouchers',
  'General',
];

// 'any' is stored as the channel value but reads as 'Both' — it means the
// response suits an email and a text equally.
export const CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'any', label: 'Both' },
];

export const CHANNEL_LABELS = { email: 'Email', sms: 'SMS', any: 'Both' };

export const TOKEN_HINTS = ['{{customer_name}}', '{{case_code}}', '{{booking_ref}}'];

// Tokens are filled from whatever the reply box knows about the customer in
// front of it. Anything we cannot resolve is left visible as the token itself,
// so a missing detail is obvious to whoever is sending rather than silently
// blanked out of the message.
export function resolveMacroTokens(text, context = {}) {
  if (!text) return '';
  return String(text).replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (match, key) => {
    const value = context[String(key).toLowerCase()];
    return value === undefined || value === null || value === '' ? match : String(value);
  });
}

// Work out the new box contents for an insertion. Existing text is never
// replaced: the snippet lands at the caret (or at the end) with a blank line
// either side, so several responses can be stacked cleanly in one message.
export function buildMacroInsert(value, text, start, end) {
  const current = value || '';
  const from = typeof start === 'number' ? start : current.length;
  const to = typeof end === 'number' ? end : current.length;
  const before = current.slice(0, from);
  const after = current.slice(to);

  const prefix = !before ? '' : before.endsWith('\n\n') ? '' : before.endsWith('\n') ? '\n' : '\n\n';
  const suffix = !after ? '' : after.startsWith('\n\n') ? '' : after.startsWith('\n') ? '\n' : '\n\n';

  return {
    next: `${before}${prefix}${text}${suffix}${after}`,
    caret: (before + prefix + text).length,
  };
}

// Reads the caret from the box when it has focus, otherwise appends at the end.
export function insertMacroInto(el, text) {
  const current = el?.value ?? '';
  const focused = typeof document !== 'undefined' && !!el && document.activeElement === el;
  const start = focused && typeof el.selectionStart === 'number' ? el.selectionStart : current.length;
  const end = focused && typeof el.selectionEnd === 'number' ? el.selectionEnd : current.length;
  return buildMacroInsert(current, text, start, end);
}

// Puts the caret back where the snippet ended, once React has re-rendered.
export function restoreCaret(el, caret) {
  if (!el || typeof el.setSelectionRange !== 'function') return;
  requestAnimationFrame(() => {
    try {
      el.focus();
      el.setSelectionRange(caret, caret);
    } catch {
      // The box may have been unmounted or switched tabs in the meantime.
    }
  });
}

// Usage tracking is a side effect of inserting, never a reason to block it.
export async function recordMacroUse(macro) {
  if (!macro?.id) return;
  try {
    await base44.entities.CrmMacro.update(macro.id, {
      usage_count: Number(macro.usage_count || 0) + 1,
      last_used_at: new Date().toISOString(),
    });
  } catch (e) {
    console.warn('[crmMacros] Could not record macro use:', e?.message || e);
  }
}